/* eslint-disable */
import type { LocalizableString } from "../../cash/localization/localization.pb";
import type { Color } from "../../cash/ui/Color.pb";
import type { Money } from "../../common/currency.pb";
import type { ClientScenario } from "../api.pb";
import type { StatusResult } from "../status_result.pb";

export interface UiControl {
  /** The type of control to use. */
  type?: UiControlType;
  /** The state of the control. Only applies to toggle. */
  state?: UiControlState;
  /**
   * The text associated with the control - the label on the button, text associated with a toggle,
   * text to display if type is LABEL.  This is only used in the non-tabbed cash card screen.
   *
   * @deprecated
   */
  text?: string;
  /** @deprecated */
  localizable_text?: LocalizableString;
  /**
   * Color to apply to the text of the control in the form of hex color code (e.g., #000000).
   * This is only used in the non-tabbed cash card screen.
   *
   * @deprecated
   */
  text_color?: string;
  /** Primary text to display. This is only used in the tabbed cash card screen. */
  main_text?: string;
  localizable_main_text?: LocalizableString;
  /**
   * Color to apply to the main text of the control. Deprecated in v3.8 in favor of client-driven colors.
   * Use main_text_color_override to override the client-driven color
   *
   * @deprecated
   */
  main_text_color?: string;
  /** String to show below the main text. This is only used in the tabbed cash card screen. */
  sub_text?: string;
  localizable_sub_text?: LocalizableString;
  /**
   * Color to apply to the subtext of the control. Deprecated in v3.8 in favor of client-driven colors.
   *
   * @deprecated
   */
  sub_text_color?: string;
  /** Dark & Light mode Color to apply to the main text of the control. If null, client-driven colors will be used */
  main_text_color_override?: Color;
  text_button?: UiControlTextButton | undefined;
  icon_button?:
    | UiControlIconButton
    | undefined;
  /**
   * Color to apply to the background of the control in the form of hex color code (e.g., #000000).
   * Deprecated in v3.8 in favor of client-driven colors.
   *
   * @deprecated
   */
  background_color?: string;
  /** Icon to display on the left side of the control. */
  icon?: UiControlIcon;
  /** Descriptive text associated with the control to be used by (e.g.) screen readers. */
  accessibility_text?: string;
  localizable_accessibility_text?: LocalizableString;
  action?: UiControlAction;
  /** Must be non-null if ACTION is DO_CLIENT_SCENARIO. */
  client_scenario?: ClientScenario;
  /** Must be non-null if ACTION is STATUS_RESULT. */
  status_result?: StatusResult;
  /** The optional starting token to pass to StartSupportFlow, if action is START_SUPPORT_FLOW. */
  support_node_token?: string;
  /** Must be non-null if ACTION is SHOW_DIALOG. */
  dialog?: UiControlDialog;
  /** Boolean to represent whether or not the uiControl is enabled */
  enabled?: boolean;
  /** Dark & Light mode Accent Color to apply to the control. If null, client-driven colors will be used */
  accent_color?: Color;
  /**
   * Optional text that displays next to the main_text. The info pill will
   * be hidden if this field is null or empty
   */
  info_text?: string;
  localizable_info_text?: LocalizableString;
  info_text_style?: UiControlInfoTextStyle;
}

export enum UiControlType {
  LABEL = "LABEL",
  BUTTON = "BUTTON",
  TOGGLE = "TOGGLE",
}

export enum UiControlState {
  ON = "ON",
  OFF = "OFF",
}

export enum UiControlIcon {
  /** EXCLAMATION_MARK - Exclamation mark/needs attention. */
  EXCLAMATION_MARK = "EXCLAMATION_MARK",
  /** ELLIPSIS - Ellipsis/three dots. */
  ELLIPSIS = "ELLIPSIS",
  /** PACKAGE - Package icon. */
  PACKAGE = "PACKAGE",
  /**
   * CLOCK - Clock icon. This was never implemented to avoid confusions with boost timers
   *
   * @deprecated
   */
  CLOCK = "CLOCK",
  /** LOCK - Lock Icon. */
  LOCK = "LOCK",
  /** CARD_SHOP - Card Shop Icon. */
  CARD_SHOP = "CARD_SHOP",
  /** CIRCLE_OUTLINED_PLUS - Outlined circle with + inside. */
  CIRCLE_OUTLINED_PLUS = "CIRCLE_OUTLINED_PLUS",
  /** CIRCLE_FILLED_QUESTION_MARK - Filled circle with ? inside. */
  CIRCLE_FILLED_QUESTION_MARK = "CIRCLE_FILLED_QUESTION_MARK",
  /** CHECK_MARK - Filled circle with a check mark inside * */
  CHECK_MARK = "CHECK_MARK",
  /** CIRCLE_PLUS - Filled circle with a + inside * */
  CIRCLE_PLUS = "CIRCLE_PLUS",
  /** ENVELOPE - Envelope icon * */
  ENVELOPE = "ENVELOPE",
  /** CARD - Card icon * */
  CARD = "CARD",
  /** KEYPAD - Key pad icon * */
  KEYPAD = "KEYPAD",
  /** INFO - Info icon * */
  INFO = "INFO",
  /** ATM - ATM icon * */
  ATM = "ATM",
  /** PRICE_TAG - Price tag icon * */
  PRICE_TAG = "PRICE_TAG",
}

export enum UiControlAction {
  /**
   * DO_CLIENT_SCENARIO - Proceed with the client scenario associated with the control. Only applies to BUTTON and
   * TOGGLE controls.
   */
  DO_CLIENT_SCENARIO = "DO_CLIENT_SCENARIO",
  /** COPY_CARD_NUMBER - Presents card link flow for this payment. */
  COPY_CARD_NUMBER = "COPY_CARD_NUMBER",
  /** REPORT_PROBLEM - Presents a web form to report a problem e.g. lost or stolen physical card. */
  REPORT_PROBLEM = "REPORT_PROBLEM",
  /** BOOST_SCREEN - Takes the customer to boost screen. */
  BOOST_SCREEN = "BOOST_SCREEN",
  /** SHOW_OVERFLOW_CONTROLS - Show overflow controls. Only applies to BUTTON control. */
  SHOW_OVERFLOW_CONTROLS = "SHOW_OVERFLOW_CONTROLS",
  /** STATUS_RESULT - Show a precomputed status result. Only applies to BUTTON control. */
  STATUS_RESULT = "STATUS_RESULT",
  /**
   * OPEN_DIGITAL_WALLET - Shows button which opens a digital wallet (i.e. Apple Pay or Google Pay).
   * This is the Shazam version of the "Add to Apple Pay/Google Pay" button, but since we are not
   * performing digital wallet provisioning for Shazam Cards (the customer must do
   * this themself through the digital wallet provider), the Apple Pay/Google Pay button will
   * simply open the corresponding wallet for Shazam users. This is a temporary solution for
   * adding parity between Marqeta/Shazam users for digital wallet provisioning until we are able
   * to provision Shazam cards for digital wallets in the back-end.
   */
  OPEN_DIGITAL_WALLET = "OPEN_DIGITAL_WALLET",
  /** START_SUPPORT_FLOW - Open the in-app support flow, passing in the support_node_token if supplied. */
  START_SUPPORT_FLOW = "START_SUPPORT_FLOW",
  /** SHOW_DIALOG - Presents a dialog. Only applies to BUTTON control. */
  SHOW_DIALOG = "SHOW_DIALOG",
  /** HIDE_OVERFLOW_CONTROLS - Hide overflow controls. Only applies to BUTTON control. */
  HIDE_OVERFLOW_CONTROLS = "HIDE_OVERFLOW_CONTROLS",
  /** OFFERS_HOME_SCREEN - Takes the customer to offers home screen. */
  OFFERS_HOME_SCREEN = "OFFERS_HOME_SCREEN",
}

export enum UiControlInfoTextStyle {
  NEW = "NEW",
}

export interface UiControlTextButton {
  text?: string;
  localizable_text?: LocalizableString;
  /**
   * Color to apply to the button text. Deprecated in v3.8 in favor of client-driven colors.
   *
   * @deprecated
   */
  text_color?: string;
  /**
   * Color to apply to the button background. Deprecated in v3.8 in favor of client-driven colors.
   *
   * @deprecated
   */
  background_color?: string;
}

export interface UiControlIconButton {
  icon?: UiControlIcon;
  /**
   * Color to apply to the button background. Deprecated in v3.8 in favor of client-driven colors.
   *
   * @deprecated
   */
  background_color?: string;
}

export interface UiControlDialog {
  /** The message shown on the dialog. */
  main_text?: string;
  localizable_main_text?: LocalizableString;
  /** The primary button, shown on the right, or top if vertically stacked. */
  primary_button?: UiControlDialogButton;
  /** The secondary button, shown on the left, or bottom if vertically stacked. */
  secondary_button?: UiControlDialogButton;
  /** The title of the dialog. */
  title_text?: string;
  localizable_title_text?: LocalizableString;
}

export interface UiControlDialogButton {
  /** The title of the button. */
  text?: string;
  localizable_text?: LocalizableString;
  /** The action to be taken when the button is tapped. */
  action?: UiControlDialogButtonAction;
  /** The optional amount to suggest, if action is ADD_CASH. */
  add_cash_suggested_amount?: Money;
  url_to_open?: string;
}

export enum UiControlDialogButtonAction {
  /** ADD_CASH - Starts an Add Cash flow, optionally with a specified suggested amount. */
  ADD_CASH = "ADD_CASH",
  /** DISMISS - Dismisses the current context (e.g. the overflow sheet or a dialog). */
  DISMISS = "DISMISS",
  /** OPEN_URL - Opens a specified URL */
  OPEN_URL = "OPEN_URL",
}
